import type { EquipmentPurpose } from '@/types/equipment'
import { useMemo } from 'react'
import { useReservationsInWindow } from '@/hooks/useProcessSteps'
import { useAssignableUsers } from '@/hooks/useAssignableUsers'
import { reservationsToTasks } from '@/lib/reservationTasks'
import { resolveActorName } from '@/lib/userDisplayName'
import type { MaintenanceTask } from '@/types/maintenanceTask'

/**
 * Live process-step equipment holds in a window, shaped as worklist rows.
 *
 * Deliberately a separate hook from `useMyList` rather than a merge inside it:
 * `useTeamSchedule` also consumes `useMyList`, and folding reservations into
 * that hook's output would have put read-only rows into the technician schedule
 * — a surface that drags, reassigns and reschedules what it is given. Composing
 * at the call site keeps the extra rows where they were asked for.
 *
 * The window is the caller's — the Tasks tab's date range drives both this and
 * the maintenance-event feed, so the two can never show different periods. Both
 * bounds are the half-open ones the endpoint expects (see `toQueryRange`).
 *
 * The worklist's own stats (due today / overdue / completed) are computed in
 * `useMyList` and so stay maintenance-only, which is correct: a booking is
 * neither due nor overdue.
 */
export function useReservationTasks(
  from: string | null,
  to: string | null,
  /** Only holds on equipment of these purposes (z8nrz7ewt2). Omitted: every purpose. */
  purposes?: readonly EquipmentPurpose[],
): { tasks: MaintenanceTask[]; isLoading: boolean; error: string | null } {
  const { data, isLoading, fetchStatus, error } = useReservationsInWindow(from, to, purposes)
  // A hold records WHO booked it as an actor string — `actorOf(user)`, so an
  // email in practice — with no user id attached. The roster is the only way
  // back to a name, which is why it is resolved here rather than in the pure
  // adapter. Already cached: the equipment tabs around this one use it too.
  const { users } = useAssignableUsers()

  const tasks = useMemo(
    () => reservationsToTasks(data ?? [], (actor) => resolveActorName(actor, users)),
    [data, users],
  )

  return {
    tasks,
    isLoading: isLoading && fetchStatus !== 'idle',
    error: error instanceof Error ? error.message : null,
  }
}
